import React, { FC, Fragment, useCallback, useEffect } from 'react'
import { FieldNames } from 'Constants/FieldNames'
import {
	deviceDetect,
	FileStatuses,
	FileWithPreview,
	getResultFile,
	modalsSlice,
	MouseEventFunction,
	prepareFileDescr,
	UploadContainerProps,
	UploadFormConnector,
	UploadDropzone,
	getFilesFromContainer,
} from 'Modules'
import {
	FilesWrapper,
	FileViewer,
	UploadContentWrapper,
	i18n,
	FileError,
	DropzoneWrapper,
} from 'LibNeo'
import { useAppDispatch, useUnmount } from 'Src/hooks'
import {
	canShowFile,
	downloadFile,
	formatDateForNewUpload,
	getActualSize,
	isImageFile,
} from 'Helpers'
import { MODAL_NAME } from 'Constants/ModalName'
import { useSelector } from 'react-redux'

const actions = {
	showModal: modalsSlice.actions.showModal,
}

export const UploadContainer: FC<UploadContainerProps> = ({
	form,
	fieldName,
	fieldOptions,
	renderFilePreview,
	renderDropzone,
	fileContainersList,
	clearFileContainersList,
	deleteFileContainer,
	error,
	setError,
	viewDispatch,
	maxFilesCount,
	actionDelete,
	multiple,
	actionCreate,
	actionUpdate,
	setFileContainersList,
	acceptedFiles,
	disabled,
	maxFileSize,
	viewParams,
	hideFilePreview,
	hideDropzone,
	hideDropzoneWhileMaxFiles,
	errorPlace,
	errorGap,
	customError,
	disableError,
	width,
}) => {
	const { showModal } = useAppDispatch(actions)
	const isMobile = useSelector(deviceDetect.getIsMobile)
	const formValue = form?.getFieldValue(fieldName)
	useEffect(() => {
		if (fieldName && form && !formValue) clearFileContainersList()
	}, [formValue])

	useUnmount(() => {
		fileContainersList?.forEach(({ file }) => {
			window.URL.revokeObjectURL(file.preview)
		})
		clearFileContainersList()
	})

	useEffect(() => {
		if (fileContainersList.length >= maxFilesCount && !multiple) {
			viewDispatch({ type: 'refresh' })
		}
		if (fileContainersList.length >= maxFilesCount && multiple) {
			viewDispatch({ type: 'lock' })
		}
	}, [fileContainersList.length, multiple, maxFilesCount])

	const deleteFileWithButtonRefs = (fileName: string) => {
		const fileContainers = deleteFileContainer(fileName)
		setError('')
		viewDispatch({ type: 'file-upload' })
		const fileList = getFilesFromContainer(fileContainers)
		if (form) {
			form.setFieldsValue({ [fieldName]: getResultFile(fileList, maxFilesCount) })
		} else actionDelete?.(fieldName as FieldNames, getResultFile(fileList, maxFilesCount))
	}

	const callbackWhileFileReject = useCallback(
		(acceptFiles) => {
			if (acceptFiles?.length) {
				setError(i18n('common.some_wrong_file_type'))
			} else {
				setError(i18n('common.wrong_file_type'))
			}
			if (!multiple) {
				clearFileContainersList()
				form?.setFieldsValue({ [fieldName]: undefined })
				viewDispatch({ type: 'file-upload' })
			}
		},
		[multiple]
	)

	const callbackWhileFileAccept = (acceptFiles) => {
		let list = fileContainersList.slice()
		if (list?.length >= maxFilesCount && multiple) {
			return
		}

		const fileListNames = fileContainersList?.map(({ file }) => file.name)
		const files = getFilesFromContainer(list)

		const notUnique = acceptFiles.some((acceptedItem) => fileListNames.includes(acceptedItem.name))
		if (notUnique) {
			setError(i18n('common.file_already_upload'))
			return
		}

		if (form) {
			form.setFieldsValue({ [fieldName]: getResultFile(acceptFiles, maxFilesCount, files) })
		} else if (!fileContainersList?.length && actionCreate) {
			actionCreate?.(fieldName as FieldNames, getResultFile(acceptFiles, maxFilesCount, files))
		} else {
			actionUpdate?.(fieldName as FieldNames, getResultFile(acceptFiles, maxFilesCount, files))
		}

		acceptFiles.forEach((file) => {
			if (list.length < maxFilesCount)
				list.push({
					file,
					status: FileStatuses.COMPLETE,
					description: prepareFileDescr([
						getActualSize(file.size),
						formatDateForNewUpload(file.created_at ?? new Date().toString()),
					]),
				})
			else if (list.length === maxFilesCount && !multiple)
				list = [
					{
						file,
						status: FileStatuses.COMPLETE,
						description: prepareFileDescr([
							getActualSize(file.size),
							formatDateForNewUpload(file.created_at ?? new Date().toString()),
						]),
					},
				]
		})

		setError('')
		setFileContainersList({
			fieldName,
			fileContainersList: list,
		})
	}

	const viewFile = useCallback(
		(file: FileWithPreview): MouseEventFunction =>
			() => {
				const { preview, name } = file
				const isImage = isImageFile(name)
				const onClickHandler = () => downloadFile(preview, name)
				if (canShowFile(name, isMobile)) {
					showModal({
						key: MODAL_NAME.FILE_PREVIEW,
						Component: FileViewer,
						componentProps: { file, onClickHandler, isImage },
						width: 'auto',
					})
				} else {
					downloadFile(preview, name)
				}
			},
		[showModal, isMobile]
	)

	const isMaxFiles = fileContainersList.length >= maxFilesCount
	const showFilePreview = !hideFilePreview
	const showDropzone = !hideDropzone && !(hideDropzoneWhileMaxFiles && isMaxFiles)

	return (
		<UploadFormConnector form={form} fieldOptions={fieldOptions} fieldName={fieldName}>
			<UploadContentWrapper>
				{showDropzone && (
					<DropzoneWrapper errorPlace={errorPlace} errorGap={errorGap} width={width}>
						<UploadDropzone
							accept={acceptedFiles}
							multiple={multiple}
							disableClick={disabled || (multiple && isMaxFiles)}
							maxSize={maxFileSize}
							callbackWhileFileAccept={callbackWhileFileAccept}
							callbackWhileFileReject={callbackWhileFileReject}
							width={width}
						>
							{renderDropzone({
								viewParams,
								disabled: disabled || (multiple && isMaxFiles),
								maxFileSize,
								acceptedFiles,
								multiple,
								maxFilesCount,
								isMaxFiles,
								error,
								setError,
							})}
						</UploadDropzone>
						{(Boolean(error) || Boolean(customError)) && !disableError && (
							<FileError>{customError ?? error}</FileError>
						)}
					</DropzoneWrapper>
				)}

				{Boolean(fileContainersList?.length && showFilePreview) && (
					<FilesWrapper>
						{fileContainersList
							.slice()
							.reverse()
							.map(
								({ file, description, status }) =>
									file && (
										<Fragment key={`${String(file?.name)}_${file?.id}`}>
											{renderFilePreview({
												file,
												description,
												status,
												deleteFile: () => deleteFileWithButtonRefs(file?.name),
												showFilePreview: () => viewFile(file),
											})}
										</Fragment>
									)
							)}
					</FilesWrapper>
				)}
			</UploadContentWrapper>
		</UploadFormConnector>
	)
}
